import React, { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

interface LiveRegionProps {
  children: React.ReactNode;
  politeness?: 'polite' | 'assertive' | 'off';
  atomic?: boolean;
  relevant?: 'additions' | 'removals' | 'text' | 'all';
  className?: string;
}

/**
 * LiveRegion Component for Dynamic Content Announcements
 * WCAG 2.1 AA compliant way to announce dynamic content changes to screen readers
 */
export const LiveRegion: React.FC<LiveRegionProps> = ({
  children,
  politeness = 'polite',
  atomic = false,
  relevant = 'additions text',
  className,
}) => {
  const regionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Ensure the live region is present in the DOM for screen readers
    if (regionRef.current) {
      // Force a re-render to announce content changes
      const content = regionRef.current.textContent;
      if (content) {
        // Clear and re-add content to trigger announcement
        regionRef.current.textContent = '';
        setTimeout(() => {
          if (regionRef.current) {
            regionRef.current.textContent = content;
          }
        }, 10);
      }
    }
  }, [children]);

  return (
    <div
      ref={regionRef}
      className={cn(
        // Position off-screen but accessible to screen readers
        "sr-only absolute left-0 top-0",
        className
      )}
      aria-live={politeness}
      aria-atomic={atomic}
      aria-relevant={relevant as React.HTMLAttributes<HTMLDivElement>['aria-relevant']}
      role="status"
    >
      {children}
    </div>
  );
};

/**
 * Hook for programmatically announcing messages to screen readers
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useAnnounce = () => {
  const announceRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Create a live region if one doesn't exist
    if (!announceRef.current) {
      const region = document.createElement('div');
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      region.className = 'sr-only absolute left-0 top-0';
      document.body.appendChild(region);
      announceRef.current = region;
    }

    return () => {
      // Cleanup on unmount
      if (announceRef.current && document.body.contains(announceRef.current)) {
        document.body.removeChild(announceRef.current);
      }
    };
  }, []);

  const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if (announceRef.current) {
      announceRef.current.setAttribute('aria-live', priority);
      announceRef.current.textContent = '';

      // Use setTimeout to ensure the change is announced
      setTimeout(() => {
        if (announceRef.current) {
          announceRef.current.textContent = message;
        }
      }, 10);
    }
  };

  return { announce };
};
